import { useQueryClient } from 'react-query'
import { GenresStreamResponse } from './useChartQuery'
import { colorHexToRgba } from 'utils'
import { useStats } from 'app/webview/pages/stats_profile/lib/ctx/stats'
import { useStatsTheme } from 'app/webview/pages/stats_profile/theme/ctx'

export type ChartItem = Record<string, number>

export const useChartData = () => {
  const [year, userId] = useStats((s) => [s.year, s.statsUserId])
  const theme = useStatsTheme((s) => s.theme)
  const client = useQueryClient()
  const periodType = 'month'
  const queryKey = ['stats-genre-stream', userId, periodType, year]
  const data = client.getQueryData<GenresStreamResponse>(queryKey)
  const results = data?.data || []

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const transformData = () => {
    const chartData: ChartItem[] = []
    const genreSet = new Set<string>()
    const monthlyData: Record<string, ChartItem> = {}
    const genreTotals: Record<string, number> = {}

    months.forEach((month) => {
      monthlyData[month] = {}
    })

    results.forEach(({ start_at, breakdown, number_achieved }) => {
      const monthIndex = new Date(start_at).getUTCMonth()
      const monthName = months[monthIndex]

      breakdown.forEach(({ genre, percentage }) => {
        const adjustedValue = Math.floor(percentage * number_achieved)

        genreSet.add(genre)
        genreTotals[genre] = (genreTotals[genre] || 0) + adjustedValue

        if (!monthlyData[monthName][genre]) {
          monthlyData[monthName][genre] = 0
        }

        monthlyData[monthName][genre] += adjustedValue
      })
    })

    const isUnknownState = genreSet.size === 0
    if (isUnknownState) {
      genreSet.add('Unknown')
      genreTotals['Unknown'] = 1
    }

    const genres = Array.from(genreSet).sort(
      (a, b) => (genreTotals[b] || 0) - (genreTotals[a] || 0)
    )

    months.forEach((month) => {
      genres.forEach((genre) => {
        if (!monthlyData[month][genre]) {
          monthlyData[month][genre] = isUnknownState ? 0 : 0.1
        }
      })
      chartData.push(monthlyData[month])
    })

    return { chartData, genres }
  }

  const { chartData, genres } = transformData()

  const monthLabels = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  const baseColors = theme.colors.charts.genreStream

  const genreColors = Object.fromEntries(
    genres.reverse().map((genre, i) => {
      const colorIndex = i % baseColors.length
      const opacityStep = Math.floor(i / baseColors.length)
      const alpha = 1 - opacityStep * 0.1

      return [genre, colorHexToRgba({ color: baseColors[colorIndex], alpha })]
    })
  )

  const isZeroState = genres.length === 1 && genres[0] === 'Unknown'

  return { data: chartData, monthLabels, genres, genreColors, isZeroState }
}
